import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import type { BasicFacility, TimeCardShift } from '../../../../../js/api';
import { ShiftRateTypesEnum, ShiftTypesEnum } from '../../../../../js/api';
import { Button } from '../../../components/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { Field, Label } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { SHIFT_TYPE_LABELS, reactSelectReset } from '../../../utils/utils';

type FacilityOption = {
  value: string;
  label: string;
  facility: BasicFacility;
};

interface ShiftEditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (shiftId: string, data: Record<string, any>) => void;
  shift?: TimeCardShift;
  facilityOptions: FacilityOption[];
  isPending?: boolean;
}

const RATE_TYPE_OPTIONS = [
  { value: ShiftRateTypesEnum.DAY, label: 'Day' },
  { value: ShiftRateTypesEnum.NIGHT, label: 'Overnight' },
  { value: ShiftRateTypesEnum.CALL, label: 'Call' },
  { value: ShiftRateTypesEnum._24H, label: '24 Hour' },
];

const SHIFT_TYPE_OPTIONS = [
  { value: ShiftTypesEnum.DAY, label: SHIFT_TYPE_LABELS[ShiftTypesEnum.DAY] || 'Day' },
  { value: ShiftTypesEnum.NIGHT, label: SHIFT_TYPE_LABELS[ShiftTypesEnum.NIGHT] || 'Overnight' },
  { value: ShiftTypesEnum._24H, label: SHIFT_TYPE_LABELS[ShiftTypesEnum._24H] || '24 Hour' },
];

export const ShiftEditModal: React.FC<ShiftEditModalProps> = ({
  open,
  onClose,
  onSave,
  shift,
  facilityOptions,
  isPending = false,
}) => {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [endOn, setEndOn] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [shiftType, setShiftType] = useState<ShiftTypesEnum>(ShiftTypesEnum.DAY);
  const [shiftRateType, setShiftRateType] = useState<ShiftRateTypesEnum>(ShiftRateTypesEnum.DAY);
  const [bonusAmount, setBonusAmount] = useState('0');
  const [holidayAmount, setHolidayAmount] = useState('0');
  const [explanation, setExplanation] = useState('');

  useEffect(() => {
    if (open && shift) {
      setStartTime(shift.start_time?.slice(0, 5) || '');
      setEndTime(shift.end_time?.slice(0, 5) || '');
      setEndOn(shift.end_on || '');
      setFacilityId(shift.facility?.id || '');
      setShiftType((shift.shift_type as ShiftTypesEnum) || ShiftTypesEnum.DAY);
      setShiftRateType((shift.shift_rate_type as ShiftRateTypesEnum) || ShiftRateTypesEnum.DAY);
      setBonusAmount(String(shift.bonus_amount ?? 0));
      setHolidayAmount(String(shift.holiday_amount ?? 0));
      setExplanation(shift.explanation || '');
    }
  }, [open, shift]);

  const handleSubmit = () => {
    if (!shift) return;
    const data: Record<string, any> = {};

    if (startTime !== shift.start_time?.slice(0, 5)) data.start_time = startTime + ':00';
    if (endTime !== shift.end_time?.slice(0, 5)) data.end_time = endTime + ':00';
    if (endOn !== shift.end_on) data.end_on = endOn;
    if (facilityId !== shift.facility?.id) data.facility = facilityId;
    if (shiftType !== shift.shift_type) data.shift_type = shiftType;
    if (shiftRateType !== shift.shift_rate_type) data.shift_rate_type = shiftRateType;
    if (Number(bonusAmount) !== (shift.bonus_amount ?? 0)) data.bonus_amount = Number(bonusAmount);
    if (Number(holidayAmount) !== (shift.holiday_amount ?? 0))
      data.holiday_amount = Number(holidayAmount);
    if (explanation !== (shift.explanation || '')) data.explanation = explanation;

    onSave(shift.id, data);
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogTitle>Edit Shift</DialogTitle>
      <DialogBody>
        <div className="tw:space-y-4">
          <div className="tw:grid tw:grid-cols-2 tw:gap-4">
            <Field>
              <Label>Start Time</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </Field>
            <Field>
              <Label>End Time</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </Field>
          </div>

          <Field>
            <Label>End Date</Label>
            <Input type="date" value={endOn} onChange={(e) => setEndOn(e.target.value)} />
          </Field>

          <Field>
            <Label>Facility</Label>
            <Select
              options={facilityOptions}
              value={facilityOptions.find((f) => f.value === facilityId) || null}
              onChange={(opt) => setFacilityId(opt?.value || '')}
              styles={reactSelectReset}
              isClearable={false}
            />
          </Field>

          <div className="tw:grid tw:grid-cols-2 tw:gap-4">
            <Field>
              <Label>Shift Type</Label>
              <Select
                options={SHIFT_TYPE_OPTIONS}
                value={SHIFT_TYPE_OPTIONS.find((o) => o.value === shiftType) || null}
                onChange={(opt) =>
                  setShiftType((opt?.value as ShiftTypesEnum) || ShiftTypesEnum.DAY)
                }
                styles={reactSelectReset}
                isClearable={false}
              />
            </Field>
            <Field>
              <Label>Rate Type</Label>
              <Select
                options={RATE_TYPE_OPTIONS}
                value={RATE_TYPE_OPTIONS.find((o) => o.value === shiftRateType) || null}
                onChange={(opt) =>
                  setShiftRateType((opt?.value as ShiftRateTypesEnum) || ShiftRateTypesEnum.DAY)
                }
                styles={reactSelectReset}
                isClearable={false}
              />
            </Field>
          </div>

          <div className="tw:grid tw:grid-cols-2 tw:gap-4">
            <Field>
              <Label>Bonus Amount</Label>
              <Input
                type="number"
                min="0"
                value={bonusAmount}
                onChange={(e) => setBonusAmount(e.target.value)}
              />
            </Field>
            <Field>
              <Label>Holiday Amount</Label>
              <Input
                type="number"
                min="0"
                value={holidayAmount}
                onChange={(e) => setHolidayAmount(e.target.value)}
              />
            </Field>
          </div>

          <Field>
            <Label>Explanation</Label>
            <textarea
              className="tw:w-full tw:rounded-lg tw:border tw:border-zinc-300 tw:px-3 tw:py-2 tw:text-sm tw:placeholder-zinc-400 tw:focus:border-brand-green-1 tw:focus:ring-1 tw:focus:ring-brand-green-1 tw:outline-none"
              rows={2}
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Optional explanation..."
            />
          </Field>
        </div>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button color="brandgreen0" onClick={handleSubmit} disabled={isPending}>
          {isPending ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
